import type { EstadoSolicitud, Solicitud, TipoSolicitud } from '../../../api/ops/clientesPrivacidad';
import type { EstadoChip } from '../../../componentes/Chip';
import type { NombreIcono } from '../../../componentes/iconos';
import type { TarjetaAyuda } from '../../../marco/cabecera';

/**
 * Textos de «Protección de datos» (2.7), tal cual del diseño (`2.7-proteccion-de-datos.html`) salvo donde una decisión o
 * la asesoría los corrige: N5, N6 y N7 literales de `modulos/clientes.md` §12.6 (G206), quién la atiende (G150), estados
 * «Programada» y «Bloqueada» (G196, G199), la opción «Bloquear por obligación legal o reclamación» (G196), la
 * conservación de las solicitudes (G198) y los plazos de conservación (G194, G204), que no tienen dibujo: los textos
 * marcados «texto a validar» son propuestas del panel.
 */
export const PERMISO_RGPD = 'clientes.rgpd.atender';

export type Grupo = 'abiertas' | 'resueltas' | 'rechazadas';

export const FILTROS: readonly { grupo: Grupo; etiqueta: string }[] = [
    { grupo: 'abiertas', etiqueta: 'Abiertas' },
    { grupo: 'resueltas', etiqueta: 'Resueltas' },
    { grupo: 'rechazadas', etiqueta: 'Rechazadas' },
];

export function grupoDe(estado: EstadoSolicitud): Grupo {
    return estado === 'resolved' ? 'resueltas' : estado === 'rejected' ? 'rechazadas' : 'abiertas';
}

export const TIPO: Record<TipoSolicitud, { icono: NombreIcono; nombre: string; corto: string; ejemplo: string }> = {
    access: { icono: 'download', nombre: 'Copia de sus datos', corto: 'Copia', ejemplo: 'Le envías todo lo que tu negocio sabe de él.' },
    rectification: { icono: 'pencil', nombre: 'Corregir sus datos', corto: 'Corrección', ejemplo: 'Corriges los datos que apuntó tu negocio.' },
    erasure: { icono: 'user-round-x', nombre: 'Suprimir sus datos', corto: 'Supresión', ejemplo: 'Se anonimiza su ficha en tu negocio.' },
};

export const VIA: Record<Solicitud['channel'], string> = {
    customer_self: 'Desde «Mis datos»',
    business_staff: 'Registrada por el negocio',
    platform_support: 'Soporte de Reservaria',
};

export const ESTADO: Record<EstadoSolicitud, [string, EstadoChip]> = {
    received: ['Por atender', 'pending'],
    scheduled: ['Programada', 'neutral'],
    in_progress: ['En curso', 'neutral'],
    extended: ['Plazo ampliado', 'pending'],
    resolved: ['Resuelta', 'confirmed'],
    blocked: ['Bloqueada por obligación legal', 'problem'],
    rejected: ['Rechazada', 'problem'],
};

export const PARTE: Record<'pending' | 'ready' | 'failed', [string, EstadoChip]> = {
    pending: ['Pendiente', 'pending'],
    ready: ['Lista', 'confirmed'],
    failed: ['Ha fallado', 'problem'],
};

export const VACIA: Record<Grupo, { titulo: string; texto: string }> = {
    abiertas: { titulo: 'No hay solicitudes abiertas.', texto: 'Cuando un cliente pida una copia, una corrección o que borres sus datos, la verás aquí con su plazo.' },
    // G198: las solicitudes se conservan identificadas y bloqueadas 3 años; después se anonimizan (texto a validar).
    resueltas: { titulo: 'Todavía no has resuelto ninguna.', texto: 'Las solicitudes atendidas se guardan aquí como prueba, bloqueadas, durante 3 años; después se anonimizan.' },
    rechazadas: { titulo: 'No has rechazado ninguna solicitud.', texto: 'Si rechazas una, quedará aquí con el motivo que recibió el cliente.' },
};

/** N5, N6 y N7 literales (`modulos/clientes.md` §12.6). */
export const N = {
    n5Etiqueta: 'Cómo comprobaste que es esa persona',
    n5Ayuda: 'Una referencia, no el documento. Por ejemplo: «DNI visto en el local por Lucía». No guardes copia del documento.',
    n5Ambito: (negocio: string) => `Solo su relación con ${negocio}. Si quiere borrar su Cuenta Reservaria, que lo pida desde «Mis datos» o escriba a Reservaria. Tienes un mes desde que la recibiste.`,
    n6Plazo: 'Tienes un mes desde que llega. Te aviso en la campana a los 20 días. Puedes ampliarlo dos meses más, una vez y con motivo.',
    n6Ampliar: 'Hasta dos meses más y una sola vez. Le avisamos por su canal preferido dentro del primer mes, con el motivo.',
    n6Rechazar: (nombre: string) => `${nombre} recibe el motivo y el aviso de que puede reclamar ante la Agencia Española de Protección de Datos. Si vuelve a pedirlo, entra como una solicitud nueva.`,
    n7: (negocio: string) => `Se cancela lo que tiene pendiente, se anonimiza su ficha en ${negocio} y le avisamos. Lo que la ley obliga a guardar se conserva bloqueado. No se puede deshacer.`,
    n7Bloquear: 'Bloquear por obligación legal o reclamación',
} as const;

export const AYUDA: Record<'lista' | 'solicitud' | 'partes' | 'registrar' | 'plazos', TarjetaAyuda[]> = {
    lista: [
        { icono: 'shield-check', titulo: 'Protección de datos', texto: 'Tus clientes pueden pedir una copia de sus datos, que los corrijas o que los borres. Las copias y supresiones que piden desde «Mis datos» las resuelve Reservaria: tú atiendes las correcciones y las que registras.' },
        { icono: 'clock', titulo: 'Plazo', texto: N.n6Plazo },
        { icono: 'lock', titulo: 'Solo tu negocio', texto: 'Atiendes su relación con tu negocio. Su Cuenta Reservaria la gestiona el propio cliente o Reservaria.' },
    ],
    solicitud: [
        { icono: 'user-round-x', titulo: 'Suprimir', texto: 'Antes te enseño qué reservas, citas o entradas se cancelan. Su ficha se anonimiza y lo que pide la ley se conserva.' },
        { icono: 'pencil', titulo: 'Corregir', texto: 'Cambias los datos que apuntó tu negocio. Los de su Cuenta Reservaria solo los cambia el cliente.' },
        { icono: 'download', titulo: 'Copia', texto: 'Cada módulo aporta su parte y el cliente recibe un enlace que caduca en 7 días.' },
    ],
    partes: [{ icono: 'layers', titulo: 'Partes', texto: 'Cada módulo prepara o borra su parte. La solicitud se resuelve cuando todos han terminado.' }],
    registrar: [{ icono: 'inbox', titulo: 'Registrar', texto: 'Para lo que te llega por email o en persona. Comprueba que es esa persona y apunta cómo lo hiciste.' }],
    // G194, G204 (texto a validar).
    plazos: [
        { icono: 'clock', titulo: 'Plazos de conservación', texto: 'Tu negocio es el responsable de las fichas de sus clientes: decide cuánto se guardan. Estos son los valores por defecto y los puedes cambiar.' },
        { icono: 'lock', titulo: 'Bloqueadas', texto: 'Las fichas bloqueadas por obligación legal o reclamación no se anonimizan hasta que se quita la marca.' },
    ],
};

export const TEXTOS = {
    soloLecturaLista: 'Aunque el negocio esté en solo lectura, las solicitudes se atienden igual: es una obligación legal.',
    soloLecturaPanel: 'El negocio está en solo lectura, pero esta solicitud la puedes atender: es una obligación legal.',
    laAtiendeReservaria: 'La atiende Reservaria',
    suprimirQuePasa: 'Su ficha en tu negocio se anonimiza: se borran sus datos, notas, campos y etiquetas, y no se puede deshacer. Primero te enseño qué reservas, citas o entradas se cancelarían.',
    suprimirConserva: 'Se conserva lo que pide la ley: pagos y facturas con importe, fecha y referencia, sin su nombre. Su Cuenta Reservaria y los demás negocios no cambian.',
    copiaQueRecibe: 'Sus datos en tu negocio (nombre, contacto, etiquetas, notas, campos, permisos e historial) y la parte de cada módulo: reservas, citas, entradas, pagos y conversaciones. Un PDF de resumen y los archivos para llevárselos a otro sitio, con un enlace de un solo uso que caduca en 7 días.',
    copiaVentana: 'Reúno sus datos de todos los módulos y le envío por su canal preferido un enlace de un solo uso. Caduca en 7 días.',
    corregirAyuda: 'Lo que el personal apuntó de esta persona. Corrige lo que pide y guarda.',
    corregirCuenta: 'Estos datos solo los cambia ella desde «Mis datos». Si el error está aquí, díselo: no los puede cambiar el negocio.',
    corregirVentana: 'Guardo los cambios en su ficha, marco la solicitud como resuelta y le aviso.',
    sinCambios: 'No has cambiado ningún dato.',
    motivoAyudaAmpliar: 'Lo recibe el cliente. Al menos 10 caracteres.',
    motivoAyudaRechazar: 'Por ejemplo: no hemos podido comprobar que eres tú. Al menos 10 caracteres.',
    partesVacias: 'Las partes aparecen cuando empiezas a atenderla.',
    partesFallo: 'Un módulo ha fallado y se reintenta solo. Si sigue fallando, Soporte de Reservaria lo revisa antes del plazo.',
    // G198 (texto a validar).
    partesHechas: 'Todas las partes están listas. La solicitud se guarda como prueba, bloqueada, durante 3 años.',
    partesEnCurso: 'La solicitud se resuelve sola cuando todos los módulos terminan.',
    recibidaAyuda: 'El plazo de un mes cuenta desde este día.',
    detallesAyuda: 'Por ejemplo, qué dato quiere que corrijas.',
    // G196 (texto a validar): lo que pasa al marcar la opción junto a N7.
    bloquearAyuda: 'La ficha no se anonimiza: queda bloqueada, sin uso, solo a disposición de jueces y autoridades, hasta que se quite la marca.',
    bloquearMotivo: 'Motivo del bloqueo',
    bloquearMotivoAyuda: 'La obligación legal o la reclamación en curso.',
    bloqueadaNota: 'Su ficha está bloqueada por obligación legal o reclamación: no se anonimiza hasta que se quite la marca.',
    programadaNota: 'Se suprime sola cuando terminen sus reservas, citas o entradas pendientes.',
} as const;

/** Plazos de conservación (G194, G204): límites del contrato. Texto a validar. */
export const PLAZOS = {
    titulo: 'Plazos de conservación',
    subtitulo: 'Cuánto guarda tu negocio las notas y las fichas sin visitas.',
    notas: 'Notas internas',
    notasNoCaducan: 'Las notas no caducan',
    notasNoCaducanAyuda: 'Si lo apagas, eliges a los cuántos meses caducan. Al caducar se borra el texto y la nota queda archivada.',
    notasMeses: 'Caducan a los (meses)',
    notasMesesAyuda: 'De 1 a 120 meses desde que se escribe cada nota.',
    fichas: 'Fichas sin visitas',
    fichasMeses: 'Se anonimizan a los (meses)',
    fichasMesesAyuda: 'De 12 a 120 meses desde la última visita; 36 por defecto. Las bloqueadas por obligación legal no se anonimizan.',
    soloLectura: 'El negocio está en solo lectura: puedes ver los plazos, pero no cambiarlos.',
    cambiado: 'Otra persona ha cambiado estos plazos. Te enseño los de ahora.',
    guardado: 'Plazos guardados. Se aplican en la siguiente pasada diaria.',
} as const;

export const primerNombre = (nombre: string) => nombre.trim().split(/\s+/)[0] ?? nombre;

/** El plazo que cuenta: el ampliado si lo hay. */
export function vence(s: Pick<Solicitud, 'due_at' | 'extended_due_at'>): string {
    return s.extended_due_at ?? s.due_at;
}
